import * as Sentry from '@sentry/vue'

/**
 * URL からクエリとハッシュを落とす。Sentry へ送る値の秘匿情報を除くために使う。
 * 相対パス・絶対 URL のどちらも受け取る。
 */
function stripQuery(url: string): string {
  return url.split('?')[0].split('#')[0]
}

/**
 * Kintone 疎通 NG により障害ページへリダイレクトしたことを Sentry へ 1 件通知する。
 *
 * - 送るのは `ng` 判定でリダイレクトしたときだけ（呼び出し側で判定済み）
 * - グローバルスコープを汚す Sentry API は使わない（ユーザー情報・コンテキスト・タグ・
 *   パンくずの設定は行わない）。`captureContext`（第2引数）でこのイベントだけにタグ・
 *   付帯情報を付ける
 * - 個人情報・秘匿情報は送らない。`extra` は `from`（route.path）のみ
 * - 決して throw しない。失敗は console.warn 1 行に留める
 */
export function reportKintoneOutageRedirect(from: string): void {
  try {
    Sentry.withScope((scope) => {
      // SDK の HttpContext 統合は request.url に window.location.href を
      // 自動で載せる。全ページ適用（0013）でこの通知が任意のページから
      // 出るようになったため、クエリを含む URL がそのまま外部へ出る。
      // /carepro-id/action?oobCode=... の oobCode は Firebase のアクション
      // コード（メール検証・パスワードリセットに使える資格情報）なので、
      // このイベントに限って URL をパスだけに差し替える。
      // 差し替えであって削除ではないのは、どのページで起きたかという
      // 調査上の価値を残すため（それは extra.from と同じ粒度で足りる）。
      scope.addEventProcessor((event) => {
        if (event.request) {
          const request = { ...event.request }
          if (request.url) {
            request.url = from
          }
          // headers.Referer は document.referrer 由来でクエリを含む。
          // /carepro-id/action?oobCode=... はフルページ遷移
          // （pages/carepro-id/action/index.vue の window.location.href 代入）
          // を行うため、遷移先で ng になるとここに oobCode が載る。
          if (request.headers && typeof request.headers.Referer === 'string') {
            request.headers = {
              ...request.headers,
              Referer: stripQuery(request.headers.Referer),
            }
          }
          event.request = request
        }
        // パンくずにも URL が載る。ナビゲーションの from/to は
        // window.location 由来でクエリを含むため、ここも落とす。
        // 種別（どのページ・どのエンドポイントか）は残す。
        if (Array.isArray(event.breadcrumbs)) {
          event.breadcrumbs = event.breadcrumbs.map((crumb) => {
            if (!crumb || !crumb.data) {
              return crumb
            }
            const data: Record<string, unknown> = { ...crumb.data }
            for (const key of ['from', 'to', 'url']) {
              if (typeof data[key] === 'string') {
                data[key] = stripQuery(data[key] as string)
              }
            }
            return { ...crumb, data }
          })
        }
        return event
      })
      scope.setLevel('warning')
      scope.setTag('feature', 'kintone-health')
      scope.setExtra('from', from)
      Sentry.captureMessage(
        'kintoneHealth: connectivity ng, redirected to outage page'
      )
    })
  } catch (e: any) {
    console.warn('[kintoneHealth] Sentry report failed', e?.message)
  }
}
